import { globalFileServer, newApiBaseUrl } from "../../../../axiosInstance";
import React, { useState, useMemo, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import axios from "axios";
import ShowCategory from "./showCategory";
import {
  buildSearchVisibleIds,
  extractCategorySlugsFromProducts,
  isInSearchVisible,
} from "./categorySearchUtils";
import {
  resetExpandedCards,
  setExpandedCardsAction,
} from "../../../server/Category";

const PRODUCT_SEARCH_MIN_LENGTH = 2;
const PRODUCT_SEARCH_DEBOUNCE_MS = 350;

const CategoriesEdit = () => {
  const dispatch = useDispatch();
  const [search, setSearch] = useState("");
  const [productMatchSlugs, setProductMatchSlugs] = useState([]);
  const productSearchRequestRef = useRef(0);
  const { categoriesState, stationId } = useSelector((state) => ({
    categoriesState: state.category.categories,
    stationId: state.user.stationId,
  }));
  const [openSections, setOpenSections] = useState({
    inactive: false,
    orphans: false,
  });

  const searchFun = (e) => {
    setSearch(e.target.value);
  };

  const clearSearch = () => {
    setSearch("");
    setProductMatchSlugs([]);
  };

  const normalizedSearch = search.trim().toLowerCase();
  const isSearchActive = normalizedSearch.length > 0;

  const searchVisibleIds = useMemo(
    () =>
      buildSearchVisibleIds(categoriesState, {
        query: normalizedSearch,
        slugMatches: productMatchSlugs,
        activeOnly: true,
      }),
    [categoriesState, normalizedSearch, productMatchSlugs],
  );

  useEffect(() => {
    if (
      !normalizedSearch ||
      normalizedSearch.length < PRODUCT_SEARCH_MIN_LENGTH
    ) {
      setProductMatchSlugs([]);
      return undefined;
    }

    if (!stationId) return undefined;

    const requestId = ++productSearchRequestRef.current;
    const timeoutId = setTimeout(async () => {
      try {
        const res = await axios.get(`${newApiBaseUrl}/products/search`, {
          headers: {
            Authorization: `Bearer ${localStorage.jwtToken}`,
            "X-Tenant": localStorage.tenant || "hamazon",
          },
          params: {
            q: normalizedSearch,
            branch: stationId,
            fields: "gtin,name",
          },
        });

        if (requestId !== productSearchRequestRef.current) return;

        const results = Array.isArray(res.data?.results)
          ? res.data.results
          : [];
        setProductMatchSlugs(extractCategorySlugsFromProducts(results));
      } catch {
        if (requestId === productSearchRequestRef.current) {
          setProductMatchSlugs([]);
        }
      }
    }, PRODUCT_SEARCH_DEBOUNCE_MS);

    return () => {
      clearTimeout(timeoutId);
      productSearchRequestRef.current += 1;
    };
  }, [normalizedSearch, stationId]);

  useEffect(() => {
    if (!isSearchActive) {
      dispatch(resetExpandedCards());
      return;
    }
    if (!searchVisibleIds?.size) {
      dispatch(resetExpandedCards());
      return;
    }

    const expandMap = {};
    searchVisibleIds.forEach((id) => {
      expandMap[id] = true;
    });
    dispatch(setExpandedCardsAction(expandMap));
  }, [isSearchActive, searchVisibleIds, dispatch]);

  const filteredCategories = useMemo(() => {
    const roots = categoriesState.filter(
      (category) =>
        category.parentId === "ROOT" && category.status === "ACTIVE",
    );
    if (!isSearchActive || !searchVisibleIds) return roots;
    return roots.filter((category) =>
      isInSearchVisible(searchVisibleIds, category),
    );
  }, [categoriesState, isSearchActive, searchVisibleIds]);

  const toggleSection = (sectionKey) => {
    setOpenSections((prevState) => ({
      ...prevState,
      [sectionKey]: !prevState[sectionKey],
    }));
  };

  const allInactiveAndDescendants = useMemo(() => {
    const inactivRootIds = new Set(
      categoriesState
        .filter((cat) => cat.status === "INACTIVE" && cat.parentId === "ROOT")
        .map((cat) => cat.id || cat.Id),
    );

    const allDescendants = new Set(inactivRootIds);
    let changed = true;
    while (changed) {
      changed = false;
      for (const cat of categoriesState) {
        const catId = cat.id || cat.Id;
        const catParentId = cat.parentId || cat.FatherId;
        if (allDescendants.has(catParentId) && !allDescendants.has(catId)) {
          allDescendants.add(catId);
          changed = true;
        }
      }
    }
    return allDescendants;
  }, [categoriesState]);

  return (
    <div className="category-edit">
      <h1 className="title-role">ניהול מחלקות</h1>
      <div className="items-container">
        <div className="flex-container">
          <div className="search">
            <input
              onChange={searchFun}
              value={search}
              type="text"
              placeholder="חיפוש מחלקה, תת-מחלקה או מוצר..."
            />
            {search ? (
              <img
                loading="lazy"
                className="close"
                onClick={clearSearch}
                src={globalFileServer + "iconcss/close.svg"}
                alt="close"
              />
            ) : (
              <img
                loading="lazy"
                src={globalFileServer + "iconcss/search.svg"}
                alt="search"
              />
            )}
          </div>
        </div>
      </div>
      {isSearchActive && filteredCategories.length === 0 && (
        <p className="category-search-empty">לא נמצאו מחלקות התואמות לחיפוש</p>
      )}
      <ShowCategory
        categoriesState={filteredCategories}
        searchVisibleIds={searchVisibleIds}
      />
      <div style={{ textAlign: "center" }}>
        <h2
          onClick={() => toggleSection("inactive")}
          style={{ cursor: "pointer" }}
        >
          קטגוריות / מקבצים לא פעילים{openSections.inactive ? " ▲" : " ▼"}
        </h2>
        {openSections.inactive && (
          <ShowCategory
            categoriesState={categoriesState?.filter((cat) => {
              const catId = cat.id || cat.Id;
              return allInactiveAndDescendants.has(catId);
            })}
            parentId="ROOT"
            level={0}
            isMainSection={false}
          />
        )}
      </div>
      <div style={{ textAlign: "center" }}>
        <h2
          onClick={() => toggleSection("orphans")}
          style={{ cursor: "pointer" }}
        >
          קטגוריות / מקבצים יתומים{openSections.orphans ? " ▲" : " ▼"}
        </h2>
        {openSections.orphans && (
          <ShowCategory
            categoriesState={categoriesState?.filter(
              (cat) => cat.parentId === null,
            )}
            parentId={null}
            level={0}
            isMainSection={false}
          />
        )}
      </div>
    </div>
  );
};

export default CategoriesEdit;
